'use client';

import { type ChangeEvent, type FC, type KeyboardEvent, type ReactNode, useCallback } from 'react';

import clsx from 'clsx';

import { CheckIcon } from '@/components/common/icons/CheckIcon';
import { type WithClassName } from '@/types/react';
import { isDefined } from '@/utils/utils';

import styles from './Checkbox.module.scss';

export type CheckboxProps = WithClassName & {
  inputId?: string;
  inputName?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  label?: ReactNode;
  onChange?: (newValue: boolean) => unknown;
};

export const Checkbox: FC<CheckboxProps> = (props) => {
  const { className, inputId, inputName, checked, defaultChecked, disabled, label, onChange } = props;

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    },
    [onChange]
  );

  const handleToggle = useCallback(
    (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || !isDefined(checked)) {
        return;
      }

      onChange?.(!checked);
    },
    [checked, onChange]
  );

  return (
    <label className={clsx(styles.root, { [styles.disabled]: disabled }, className)}>
      <input
        className={styles.input}
        id={inputId}
        name={inputName}
        type="checkbox"
        disabled={disabled}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={handleChange}
      />

      <div className={styles.checkboxContainer}>
        <div className={styles.checkbox} tabIndex={0} onKeyDown={handleToggle}>
          {checked && <CheckIcon className={styles.icon} strokeWidth={3} />}
        </div>
      </div>

      {label && <div className={styles.label}>{label}</div>}
    </label>
  );
};
